import React from "react";
import {
  Box,
  Chip,
  ChipProps,
  fade,
  Typography,
  withStyles,
} from "@material-ui/core";
import { InlineType } from "../InlineType";
import { AccountStatus } from "../../api/services";

export interface AccountStatusChipProps {
  status: AccountStatus;
  disableMargin?: boolean;
}
const ClosedChip = withStyles((theme) => ({
  root: {
    background: theme.palette.error.main,
    color: theme.palette.common.white,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    "&:first-child": {
      marginLeft: 0,
    },
  },
}))(Chip);
const CancelledChip = withStyles((theme) => ({
  root: {
    background: fade(theme.palette.text.secondary, 0.7),
    color: theme.palette.common.white,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    "&:first-child": {
      marginLeft: 0,
    },
  },
}))(Chip);
const ClosingChip = withStyles((theme) => ({
  root: {
    border: `2px solid ${theme.palette.error.main}`,
    background: "none",
    color: theme.palette.text.primary,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    "&:first-child": {
      marginLeft: 0,
    },
  },
}))(Chip);

type AccountStatusChip = {
  size: ChipProps["size"];
  label: ChipProps["label"];
  component: React.FC<ChipProps>;
};
const ACCOUNT_STATUS_CHIP_MAP: Record<
  AccountStatusChipProps["status"],
  AccountStatusChip
> = {
  Closed: {
    size: "small" as const,
    label: "Account closed",
    component: ClosedChip as React.FC<ChipProps>,
  },
  Cancelled: {
    size: "small" as const,
    label: "Account cancelled",
    component: CancelledChip as React.FC<ChipProps>,
  },
  PendingTermination: {
    size: "small" as const,
    label: "Account closing",
    component: ClosingChip as React.FC<ChipProps>,
  },
  Terminated: {
    size: "small" as const,
    label: "Account closing",
    component: ClosingChip as React.FC<ChipProps>,
  },
};

/**
 * Display a chip to represent the status of an account
 * @param status - Status of the account
 * @param disableMargin - Remove the default right margin used to separate chips
 */
export const AccountStatusChip: React.FC<AccountStatusChipProps> = ({
  status,
  disableMargin,
}) => {
  const { size, label, component: Component } =
    ACCOUNT_STATUS_CHIP_MAP[status] ?? {};

  return Component ? (
    <Box
      display="inline-block"
      my={disableMargin ? 0 : 0.5}
      data-testid="AccountStatusChip"
    >
      <Component
        size={size}
        label={
          <Typography variant="caption">
            <InlineType fontWeight="bold" textDecoration="none">
              {label}
            </InlineType>
          </Typography>
        }
      />
    </Box>
  ) : null;
};
